'use client';

import React, { createContext, useContext, useCallback, useEffect, useRef, useEffectEvent } from 'react';
import { useConnection } from 'wagmi';
import { readContract } from 'wagmi/actions';
import type { Address } from 'viem';
import type { ChainType } from '@/constants/chain-specific';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import type { DexConfig } from '@/constants/project-specific/farms/types';
import { config } from '@/config/index';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import { saveChainPositions } from '@/lib/liquidity/cross-chain-positions';
import { KnownPairsCache } from '@/lib/gecko-terminal/pools';

// Pair ABI for checking balances
const PAIR_ABI = [{
  inputs: [{
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;
export interface DexLoadingState {
  dexSlug: string;
  dexName: string;
  dexLogo: string;
  status: 'pending' | 'loading-top-pools' | 'loading-all-pairs' | 'completed' | 'error';
  totalPairs: number;
  checkedPairs: number;
  foundPositions: number;
  error?: string;
}
export interface LiquidityPositionsState {
  positions: LiquidityPosition[];
  dexStates: Map<string, DexLoadingState>;
  totalValueUsd: number;
  totalFeesUsd: number;
  isLoading: boolean;
  isComplete: boolean;
  error: Error | null;
}
interface LiquidityPositionsContextValue extends LiquidityPositionsState {
  startLoading: (chainId: ChainType) => Promise<void>;
  refetch: () => Promise<void>;
  refetchPosition: (pairAddress: string, chainId: number, dexSlug: string) => Promise<void>;
  removePosition: (positionId: string) => void;
  updatePosition: (position: LiquidityPosition) => void;
  clearPositions: () => void;
}
const LiquidityPositionsContext = createContext<LiquidityPositionsContextValue | undefined>(undefined);
export function useLiquidityPositionsContext() {
  const context = useContext(LiquidityPositionsContext);
  if (!context) {
    throw new Error("useLiquidityPositionsContext must be used within LiquidityPositionsProvider");
  }
  return context;
}
interface LiquidityPositionsProviderProps {
  children: React.ReactNode;
  chainId?: ChainType;
  /** If true, positions will not be loaded automatically on mount. Call startLoading() manually. */
  deferLoading?: boolean;
}
export function LiquidityPositionsProvider({
  children,
  chainId: initialChainId = 109,
  deferLoading = false
}: LiquidityPositionsProviderProps) {
  const {
    address
  } = useConnection();
  console.log('[LiquidityContext] ========================================');
  console.log('[LiquidityContext] Provider mounted/rendered');
  console.log('[LiquidityContext] Chain ID:', initialChainId);
  console.log('[LiquidityContext] Wallet address:', address);
  console.log('[LiquidityContext] Address connected:', !!address);
  console.log('[LiquidityContext] ========================================');
  const [state, setState] = React.useState<LiquidityPositionsState>({
    positions: [],
    dexStates: new Map(),
    totalValueUsd: 0,
    totalFeesUsd: 0,
    isLoading: false,
    isComplete: false,
    error: null
  });
  const loadingRef = useRef(false);
  const currentChainRef = useRef<ChainType>(initialChainId);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Add position to state (deduplicated)
  const addPosition = useCallback((position: LiquidityPosition) => {
    setState(prev => {
      const positionId = `${position.contractAddress}-${position.token0.address}-${position.token1.address}-${position.id}`;
      const isDuplicate = prev.positions.some(p => `${p.contractAddress}-${p.token0.address}-${p.token1.address}-${p.id}` === positionId);
      if (isDuplicate) return prev;
      const newPositions = [...prev.positions, position];
      const totalValueUsd = newPositions.reduce((sum, p_0) => sum + p_0.totalValueUsd, 0);
      const totalFeesUsd = newPositions.reduce((sum_0, p_1) => sum_0 + p_1.feesValueUsd, 0);
      return {
        ...prev,
        positions: newPositions,
        totalValueUsd,
        totalFeesUsd
      };
    });
  }, []);

  // Remove position from state by ID
  const removePosition = useCallback((positionId_0: string) => {
    setState(prev_0 => {
      const newPositions_0 = prev_0.positions.filter(p_2 => p_2.id !== positionId_0);
      const totalValueUsd_0 = newPositions_0.reduce((sum_1, p_3) => sum_1 + p_3.totalValueUsd, 0);
      const totalFeesUsd_0 = newPositions_0.reduce((sum_2, p_4) => sum_2 + p_4.feesValueUsd, 0);
      return {
        ...prev_0,
        positions: newPositions_0,
        totalValueUsd: totalValueUsd_0,
        totalFeesUsd: totalFeesUsd_0
      };
    });
  }, []);

  // Update a specific position (useful after adding/removing liquidity)
  const updatePosition = useCallback((position_0: LiquidityPosition) => {
    setState(prev_1 => {
      const index = prev_1.positions.findIndex(p_5 => p_5.id === position_0.id);
      if (index === -1) {
        // Position doesn't exist, add it
        const newPositions_1 = [...prev_1.positions, position_0];
        const totalValueUsd_1 = newPositions_1.reduce((sum_3, p_6) => sum_3 + p_6.totalValueUsd, 0);
        const totalFeesUsd_1 = newPositions_1.reduce((sum_4, p_7) => sum_4 + p_7.feesValueUsd, 0);
        return {
          ...prev_1,
          positions: newPositions_1,
          totalValueUsd: totalValueUsd_1,
          totalFeesUsd: totalFeesUsd_1
        };
      } else {
        // Position exists, update it
        const newPositions_2 = [...prev_1.positions];
        newPositions_2[index] = position_0;
        const totalValueUsd_2 = newPositions_2.reduce((sum_5, p_8) => sum_5 + p_8.totalValueUsd, 0);
        const totalFeesUsd_2 = newPositions_2.reduce((sum_6, p_9) => sum_6 + p_9.feesValueUsd, 0);
        return {
          ...prev_1,
          positions: newPositions_2,
          totalValueUsd: totalValueUsd_2,
          totalFeesUsd: totalFeesUsd_2
        };
      }
    });
  }, []);

  // Update DEX state
  const updateDexState = useCallback((dexSlug: string, update: Partial<DexLoadingState>) => {
    setState(prev_2 => {
      const newDexStates = new Map(prev_2.dexStates);
      const current = newDexStates.get(dexSlug);
      if (current) {
        newDexStates.set(dexSlug, {
          ...current,
          ...update
        });
      }
      return {
        ...prev_2,
        dexStates: newDexStates
      };
    });
  }, []);

  // Clear all positions and reset state
  const clearPositions = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState({
      positions: [],
      dexStates: new Map(),
      totalValueUsd: 0,
      totalFeesUsd: 0,
      isLoading: false,
      isComplete: false,
      error: null
    });
    loadingRef.current = false;
  }, []);

  // Reset state helper (extracted for reuse)
  const resetState = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState({
      positions: [],
      dexStates: new Map(),
      totalValueUsd: 0,
      totalFeesUsd: 0,
      isLoading: false,
      isComplete: false,
      error: null
    });
    loadingRef.current = false;
  }, []);

  /**
   * Effect Event for loading positions - this is non-reactive logic that always
   * reads the latest address/chainId without causing the Effect to re-run.
   * 
   * Per React 19 best practices, useEffectEvent is used for:
   * - Logic that needs to read latest props/state
   * - But shouldn't cause the Effect to re-synchronize
   * 
   * @see https://react.dev/reference/react/useEffectEvent
   */
  const onLoadPositions = useEffectEvent(async (chainId: ChainType) => {
    if (!address || loadingRef.current) {
      console.log('[LiquidityContext] Skipping load:', {
        address,
        loading: loadingRef.current
      });
      return;
    }
    console.log('[LiquidityContext] Starting load for chain:', chainId, 'address:', address);

    // Cancel any existing loading
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    loadingRef.current = true;
    currentChainRef.current = chainId;

    // Reset state for new load
    setState({
      positions: [],
      dexStates: new Map(),
      totalValueUsd: 0,
      totalFeesUsd: 0,
      isLoading: true,
      isComplete: false,
      error: null
    });
    try {
      // Import the progressive loading function dynamically to avoid circular deps
      const {
        loadPositionsProgressively
      } = await import('@/hooks/liquidity/useProgressiveLiquidityLoader');
      console.log('[LiquidityContext] Starting progressive loading...');
      await loadPositionsProgressively({
        chainId,
        address,
        onDexStart: (dexSlug_0: string, dexName: string, dexLogo: string) => {
          console.log('[LiquidityContext] DEX started:', dexSlug_0, dexName);
          setState(prev_4 => {
            const newDexStates_0 = new Map(prev_4.dexStates);
            newDexStates_0.set(dexSlug_0, {
              dexSlug: dexSlug_0,
              dexName,
              dexLogo,
              status: 'loading-top-pools',
              totalPairs: 0,
              checkedPairs: 0,
              foundPositions: 0
            });
            return {
              ...prev_4,
              dexStates: newDexStates_0
            };
          });
        },
        onDexProgress: (dexSlug_1: string, update_0: Partial<DexLoadingState>) => {
          console.log('[LiquidityContext] DEX progress:', dexSlug_1, update_0);
          updateDexState(dexSlug_1, update_0);
        },
        onPositionFound: (position_1: LiquidityPosition) => {
          console.log('[LiquidityContext] Position found:', position_1.id);
          addPosition(position_1);
          // Update the DEX's found positions count using functional update
          const dexSlug_2 = position_1.dex;
          setState(prev_5 => {
            const newDexStates_1 = new Map(prev_5.dexStates);
            const current_0 = newDexStates_1.get(dexSlug_2);
            if (current_0) {
              newDexStates_1.set(dexSlug_2, {
                ...current_0,
                foundPositions: current_0.foundPositions + 1
              });
            }
            return {
              ...prev_5,
              dexStates: newDexStates_1
            };
          });
        },
        onDexComplete: (dexSlug_3: string) => {
          console.log('[LiquidityContext] DEX complete:', dexSlug_3);
          updateDexState(dexSlug_3, {
            status: 'completed'
          });
        },
        onDexError: (dexSlug_4: string, error_0: string) => {
          console.error('[LiquidityContext] DEX error:', dexSlug_4, error_0);
          updateDexState(dexSlug_4, {
            status: 'error',
            error: error_0
          });
        },
        abortSignal: abortControllerRef.current.signal
      });
      console.log('[LiquidityContext] Loading complete!');

      // Save positions to cross-chain storage for viewing on other chains
      setState(prev_6 => {
        if (address && prev_6.positions.length > 0) {
          saveChainPositions(address, chainId, prev_6.positions);
          console.log(`[LiquidityContext] Saved ${prev_6.positions.length} positions to cross-chain storage`);
        }
        return {
          ...prev_6,
          isLoading: false,
          isComplete: true
        };
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[LiquidityContext] Position loading aborted');
      } else {
        console.error('[LiquidityContext] Error loading positions:', error);
        setState(prev_3 => ({
          ...prev_3,
          isLoading: false,
          isComplete: true,
          error: error instanceof Error ? error : new Error('Unknown error')
        }));
      }
    } finally {
      loadingRef.current = false;
    }
  });

  // Public startLoading function that wraps the Effect Event
  const startLoading = useCallback(async (chainId_0: ChainType) => {
    await onLoadPositions(chainId_0);
  }, [onLoadPositions]);

  // Refetch positions (clears known pairs cache to force full rediscovery)
  const refetch = useCallback(async () => {
    console.log('[LiquidityContext] Refetching positions - clearing known pairs cache and reloading...');

    // Clear known pairs cache for this user/chain to force full rediscovery
    if (typeof window !== 'undefined' && address) {
      const knownPairsCache = new KnownPairsCache(currentChainRef.current, address);
      knownPairsCache.clear();
      console.log('[LiquidityContext] Cleared known pairs cache');
    }
    clearPositions();
    await startLoading(currentChainRef.current);
  }, [address, clearPositions, startLoading]);

  // Refetch a single position by pair address (efficient for add/remove liquidity)
  const refetchPosition = useCallback(async (pairAddress: string, chainId_1: number, dexSlug_5: string) => {
    if (!address) {
      console.log('[LiquidityContext] No address, skipping refetch');
      return;
    }
    console.log(`[LiquidityContext] Refetching position for pair ${pairAddress} on ${dexSlug_5}`);
    try {
      // Get DEX configuration from DEX_LIST
      const dexConfig = DEX_LIST[dexSlug_5];
      if (!dexConfig) {
        console.error(`[LiquidityContext] DEX config not found for ${dexSlug_5}`);
        return;
      }

      // Import the loader
      const {
        getPairDetails
      } = await import('@/hooks/liquidity/useProgressiveLiquidityLoader');

      // Check the pair balance
      const balance = (await readContract(config, {
        address: pairAddress as `0x${string}`,
        abi: PAIR_ABI,
        functionName: 'balanceOf',
        args: [address],
        chainId: chainId_1
      })) as bigint;
      const balanceBigInt = balance || 0n;
      if (balanceBigInt === 0n) {
        // Position no longer exists, remove it
        console.log(`[LiquidityContext] Position ${pairAddress} has zero balance, removing`);
        // Find position by contract address and remove it
        const positionToRemove = state.positions.find(p_10 => p_10.contractAddress.toLowerCase() === pairAddress.toLowerCase());
        if (positionToRemove) {
          removePosition(positionToRemove.id);
        }
        return;
      }

      // Position still exists with balance, get updated details
      const updatedPosition = await getPairDetails(pairAddress as `0x${string}`, address, chainId_1, dexSlug_5, dexConfig.name);
      if (updatedPosition) {
        console.log(`[LiquidityContext] Position ${pairAddress} updated`);
        updatePosition(updatedPosition);
      }
    } catch (error_1) {
      console.error(`[LiquidityContext] Error refetching position ${pairAddress}:`, error_1);
    }
  }, [address, state.positions, removePosition, updatePosition]);

  /**
   * Synchronize positions with wallet/chain changes
   * 
   * This Effect runs when:
   * - address changes (wallet connect/disconnect/switch)
   * - initialChainId changes (chain switch)
   * 
   * The actual loading logic is in onLoadPositions (useEffectEvent),
   * which always reads the latest values without being a dependency.
   * This follows React 19 best practices for separating reactive
   * dependencies from non-reactive logic.
   * 
   * @see https://react.dev/learn/separating-events-from-effects
   */
  useEffect(() => {
    console.log('[LiquidityContext] useEffect triggered', {
      address,
      initialChainId,
      deferLoading,
      hasAddress: !!address,
      hasChainId: !!initialChainId
    });

    // Abort any in-progress loading when dependencies change
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state when wallet/chain changes
    resetState();

    // Skip auto-loading if deferLoading is true
    // Consumer can call startLoading() manually when needed
    if (deferLoading) {
      console.log('[LiquidityContext] deferLoading=true, skipping auto-load');
      return;
    }

    // Load positions if we have both address and chainId
    if (address && initialChainId) {
      console.log('[LiquidityContext] Starting load for new wallet/chain...');
      onLoadPositions(initialChainId);
    } else {
      console.log('[LiquidityContext] No address or chainId, positions cleared');
    }

    // Cleanup: abort loading when Effect re-runs or unmounts
    return () => {
      if (abortControllerRef.current) {
        console.log('[LiquidityContext] Cleanup: aborting load');
        abortControllerRef.current.abort();
      }
    };
  }, [address, initialChainId]); // onLoadPositions is an Effect Event, not a dependency

  const value: LiquidityPositionsContextValue = {
    ...state,
    startLoading,
    refetch,
    refetchPosition,
    removePosition,
    updatePosition,
    clearPositions
  };
  return <LiquidityPositionsContext.Provider value={value}>
            {children}
        </LiquidityPositionsContext.Provider>;
}